import { reactive } from "vue";
import { defaultCertificate as buildCertificate, migrateCertificate } from "@/utils/certificateModel";
import { defaultReceipt as buildReceipt, migrateReceipt } from "@/utils/receiptModel";
import {
  createSchoolCertificateConfig,
  createSchoolReceiptConfig,
  getSchoolCertificateConfig,
  getSchoolConfig,
  getSchoolReceiptConfig,
  setSfMode,
  updateSchoolCertificateConfig,
  updateSchoolReceiptConfig,
} from "@/utils/services";
import { extractConfigId, fromApiPayload, toApiPayload } from "@/utils/certificateApi";
import {
  extractConfigId as extractReceiptConfigId,
  fromApiPayload as receiptFromApiPayload,
  toApiPayload as receiptToApiPayload,
} from "@/utils/receiptApi";

/**
 * Classroom module setup provider.
 *
 * Mimics the "services.js" contract (async functions returning data) so the
 * screens can later be plugged on the real backend without any change:
 * simply replace the localStorage read/write by the axios calls.
 */

const STORAGE_KEY = "classroom_setup";
/* Identifiant de la configuration serveur du certificat. */
const CERTIFICATE_ID_KEY = "certificate_config_id";
/* Identifiant de la configuration serveur du reçu de paiement. */
const RECEIPT_ID_KEY = "receipt_config_id";

export const CLASSROOM_MODE = {
  SCHOOL: "school", // Mode manuel / École (comportement actuel)
  COLLABORATIVE: "collaborative", // Mode collaboratif / Parent (ESKA)
};

/* Le modèle du certificat vit dans son propre fichier : classroomSetup ne
 * s'occupe que de la persistance. Ré-exporté ici pour ne pas casser les
 * imports existants. */
export {
  CERTIFICATE_TEMPLATES,
  CERTIFICATE_PALETTES,
  defaultCertificate,
} from "@/utils/certificateModel";

/* Idem pour le reçu de paiement : le modèle vit dans receiptModel.js. */
export {
  RECEIPT_TEMPLATES,
  RECEIPT_FORMATS,
  RECEIPT_PALETTES,
  defaultReceipt,
} from "@/utils/receiptModel";

function defaultSetup() {
  return {
    has_configured_classroom: false,
    mode: CLASSROOM_MODE.SCHOOL,
    allow_parent_student_creation: true,
    allow_parent_finance_configuration: true,
    // Indépendant du mode : le certificat se configure depuis sa propre page.
    certificate_configured: false,
    /* Modèle de bulletin de notes, configuré une fois par l'établissement. */
    /*
     * Modèle de bulletin. `null` tant qu'aucun n'a été enregistré : la valeur
     * par défaut est construite par l'écran de configuration, qui possède le
     * modèle. Éviter d'importer reportCardModel ici garde ce fichier utile
     * même sans la fonctionnalité bulletin.
     */
    report_card: null,
    report_card_configured: false,
    /**
     * Option du mode collaboratif. Faux par défaut : le certificat n'est
     * proposé que pour les élèves à jour de paiement. Activé, il devient
     * disponible pour tous, quel que soit le statut — utile aux
     * établissements qui ne conditionnent pas la délivrance du document au
     * règlement de la scolarité.
     */
    // Option spécifique au mode collaboratif
    allow_certificate_for_all: true,
    // Option spécifique au mode "École" (Mode manuel) — découplée de la
    // précédente pour que les deux switches soient indépendants.
    allow_certificate_for_all_school: true,
    certificate: buildCertificate(),
    /*
     * Modèle de reçu de paiement, configuré une fois par l'établissement.
     * Indépendant du mode : un reçu est émis dans les deux modes.
     */
    receipt: buildReceipt(),
    receipt_configured: false,
    /*
     * Vrai quand le modèle n'a pu être enregistré QUE localement, faute
     * d'endpoint côté serveur. Sert à ne pas effacer la configuration au
     * rechargement — voir performReceiptSave().
     */
    receipt_local_only: false,
    updated_on: null,
  };
}

function readCertificateId() {
  try {
    return localStorage.getItem(CERTIFICATE_ID_KEY) || null;
  } catch (error) {
    return null;
  }
}

function readReceiptId() {
  try {
    return localStorage.getItem(RECEIPT_ID_KEY) || null;
  } catch (error) {
    return null;
  }
}

function read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSetup();
    const parsed = JSON.parse(raw);
    const merged = {
      ...defaultSetup(),
      ...parsed,
      certificate: migrateCertificate(parsed.certificate),
      receipt: migrateReceipt(parsed.receipt),
    };
    if (parsed.mode === CLASSROOM_MODE.SCHOOL) {
      merged.allow_parent_student_creation = true;
      merged.allow_parent_finance_configuration = true;
      merged.allow_certificate_for_all_school = true;

    }
    return merged;
  } catch (err) {
    console.error("Failed to read classroom setup", err);
    return defaultSetup();
  }
}

/** Reactive singleton shared by every screen of the Classroom module. */
export const classroomSetup = reactive(read());

export function hasConfiguredClassroom() {
  return classroomSetup.has_configured_classroom === true;
}

export function isCollaborativeMode() {
  return (
    classroomSetup.has_configured_classroom &&
    classroomSetup.mode === CLASSROOM_MODE.COLLABORATIVE
  );
}

export function parentCanConfigureFinance() {
  return (
    isCollaborativeMode() && classroomSetup.allow_parent_finance_configuration === true
  );
}

/** Le modèle de bulletin a-t-il déjà été configuré ? */
export function isReportCardConfigured() {
  return classroomSetup.report_card_configured === true;
}

/** Modèle de bulletin courant. */
export function getReportCard() {
  return classroomSetup.report_card;
}

/**
 * Enregistre le modèle de bulletin.
 */
export async function saveReportCard(card) {
  classroomSetup.report_card = { ...card };
  classroomSetup.report_card_configured = true;
  classroomSetup.updated_on = new Date().toISOString();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(classroomSetup));
  return classroomSetup.report_card;
}

/**
 * Le module certificat est-il disponible ?
 */
export function isCertificateAvailable() {
  return true;
}

export function isCertificateConfigured() {
  return isCertificateAvailable() && classroomSetup.certificate_configured === true;
}

export function canIssueCertificate(student) {
  // isCertificateConfigured() porte déjà la condition « mode collaboratif ».
  if (!isCertificateConfigured()) return false;
  // Selon le mode actuel, on regarde l'option dédiée.
  if (classroomSetup.mode === CLASSROOM_MODE.SCHOOL) {
    if (classroomSetup.allow_certificate_for_all_school === true) return true;
  } else {
    if (classroomSetup.allow_certificate_for_all === true) return true;
  }
  return String((student || {}).payment_status || "").toLowerCase() === "settled";
}

/** Certificat courant (déjà migré au bon format par read()). */
export function getCertificate() {
  return classroomSetup.certificate;
}

let certificateConfigId = null;
let pendingLoad = null;
let pendingSave = null;


async function ensureConfigId() {
  if (certificateConfigId) return certificateConfigId;
  try {
    const existing = await getSchoolCertificateConfig();
    const id = extractConfigId(existing);
    if (id) {
      certificateConfigId = id;
      localStorage.setItem(CERTIFICATE_ID_KEY, id);
      return id;
    }
    // Une configuration existe mais sans identifiant exploitable : on passe
    // quand même en mise à jour, l'URL de collection servant d'upsert. Créer
    // ici produirait un second enregistrement pour la même école.
    return existing ? "" : null;
  } catch (error) {
    console.error("Failed to check existing certificate config", error);
    // Serveur injoignable : on ne crée rien à l'aveugle, on laisse remonter
    // l'erreur plutôt que de risquer un doublon.
    throw error;
  }
}

async function performSave(certificate) {
  const payload = toApiPayload(certificate);
  const knownId = await ensureConfigId();

  // knownId === null → aucune configuration : création.
  // knownId === ""   → configuration existante sans id : mise à jour.
  const saved =
    knownId === null
      ? await createSchoolCertificateConfig(payload)
      : await updateSchoolCertificateConfig(knownId || null, payload);

  let id = extractConfigId(saved);
  if (!id) {
    // La réponse ne porte pas d'identifiant : on le récupère tout de suite.
    // Sans cela, le prochain enregistrement repartirait sur un POST et
    // créerait un doublon.
    try {
      id = extractConfigId(await getSchoolCertificateConfig());
    } catch (error) {
      console.error("Failed to recover certificate config id", error);
    }
  }
  if (id) {
    certificateConfigId = id;
    localStorage.setItem(CERTIFICATE_ID_KEY, id);
  }

  classroomSetup.certificate = { ...certificate };
  classroomSetup.certificate_configured = true;
  classroomSetup.updated_on = new Date().toISOString();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(classroomSetup));
  return classroomSetup.certificate;
}

export async function saveCertificate(certificate) {
  if (pendingSave) return pendingSave;

  pendingSave = performSave(certificate).finally(() => {
    pendingSave = null;
  });
  return pendingSave;
}

async function performLoad(locale) {
  try {
    const data = await getSchoolCertificateConfig();

    if (!data) {
      // Le serveur fait foi : aucune configuration n'existe pour cette école.
      classroomSetup.certificate_configured = false;
      certificateConfigId = null;
      localStorage.removeItem(CERTIFICATE_ID_KEY);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(classroomSetup));
      return null;
    }

    const id = extractConfigId(data);
    if (id) {
      certificateConfigId = id;
      localStorage.setItem(CERTIFICATE_ID_KEY, id);
    }

    classroomSetup.certificate = fromApiPayload(data, locale);
    classroomSetup.certificate_configured = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(classroomSetup));
    return classroomSetup.certificate;
  } catch (error) {
    console.error("Failed to load certificate config", error);
    return classroomSetup.certificate_configured ? classroomSetup.certificate : null;
  }
}

export async function loadCertificateFromApi(locale) {
  if (pendingLoad) return pendingLoad;

  pendingLoad = performLoad(locale).finally(() => {
    pendingLoad = null;
  });
  return pendingLoad;
}

/* -------------------------------------------------------------------------
 * Reçu de paiement
 * ---------------------------------------------------------------------- */

export function isReceiptAvailable() {
  return true;
}

/** Le modèle de reçu a-t-il déjà été configuré et enregistré ? */
export function isReceiptConfigured() {
  return isReceiptAvailable() && classroomSetup.receipt_configured === true;
}

/** Modèle de reçu courant. */
export function getReceipt() {
  return classroomSetup.receipt;
}

let receiptConfigId = null;

/* Requêtes en cours, partagées entre les appelants — voir le certificat. */
let pendingReceiptLoad = null;
let pendingReceiptSave = null;

async function ensureReceiptConfigId() {
  if (receiptConfigId) return receiptConfigId;
  try {
    const existing = await getSchoolReceiptConfig();
    const id = extractReceiptConfigId(existing);
    if (id) {
      receiptConfigId = id;
      localStorage.setItem(RECEIPT_ID_KEY, id);
      return id;
    }
    // Une configuration existe mais sans identifiant exploitable : on passe
    // quand même en mise à jour, l'URL de collection servant d'upsert.
    return existing ? "" : null;
  } catch (error) {
    console.error("Failed to check existing receipt config", error);
    // Serveur injoignable : on ne crée rien à l'aveugle.
    throw error;
  }
}


function isEndpointMissing(error) {
  const status = error && error.response && error.response.status;
  return status === 404 || status === 405;
}

/** Écrit le modèle dans le cache local et marque la configuration faite. */
function cacheReceipt(receipt, localOnly) {
  classroomSetup.receipt = { ...receipt };
  classroomSetup.receipt_configured = true;
  classroomSetup.receipt_local_only = localOnly;
  classroomSetup.updated_on = new Date().toISOString();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(classroomSetup));
  return classroomSetup.receipt;
}

async function performReceiptSave(receipt) {
  const payload = receiptToApiPayload(receipt);

  let knownId;
  try {
    knownId = await ensureReceiptConfigId();
  } catch (error) {

    if (!isEndpointMissing(error)) throw error;
    console.warn("Receipt config endpoint unavailable — saved locally only");
    return cacheReceipt(receipt, true);
  }

  let saved;
  try {

    saved =
      knownId === null
        ? await createSchoolReceiptConfig(payload)
        : await updateSchoolReceiptConfig(knownId || null, payload);
  } catch (error) {
    if (!isEndpointMissing(error)) throw error;
    console.warn("Receipt config endpoint unavailable — saved locally only");
    return cacheReceipt(receipt, true);
  }

  let id = extractReceiptConfigId(saved);
  if (!id) {
    // La réponse ne porte pas d'identifiant : on le récupère tout de suite,
    // sans quoi le prochain enregistrement repartirait sur un POST.
    try {
      id = extractReceiptConfigId(await getSchoolReceiptConfig());
    } catch (error) {
      console.error("Failed to recover receipt config id", error);
    }
  }
  if (id) {
    receiptConfigId = id;
    localStorage.setItem(RECEIPT_ID_KEY, id);
  }

  return cacheReceipt(receipt, false);
}

/**
 * Enregistre le modèle de reçu côté serveur, puis en cache local.
 *
 * L'écriture locale n'a lieu qu'après le succès de l'appel : en cas d'échec
 * réseau, le cache n'est pas marqué « configuré » et l'utilisateur voit
 * l'erreur plutôt que de croire son travail sauvegardé.
 */
export async function saveReceipt(receipt) {
  if (pendingReceiptSave) return pendingReceiptSave;

  pendingReceiptSave = performReceiptSave(receipt).finally(() => {
    pendingReceiptSave = null;
  });
  return pendingReceiptSave;
}

async function performReceiptLoad() {
  try {
    const data = await getSchoolReceiptConfig();

    if (!data) {
      /*
       * Le modèle n'a jamais pu atteindre le serveur (endpoint absent) : le
       * cache local fait alors foi. Sans cette exception, un rechargement de
       * page effacerait une configuration que l'utilisateur vient de saisir.
       */
      if (classroomSetup.receipt_local_only) return classroomSetup.receipt;

      // Le serveur fait foi : aucune configuration n'existe pour cette école.
      classroomSetup.receipt_configured = false;
      receiptConfigId = null;
      localStorage.removeItem(RECEIPT_ID_KEY);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(classroomSetup));
      return null;
    }

    const id = extractReceiptConfigId(data);
    if (id) {
      receiptConfigId = id;
      localStorage.setItem(RECEIPT_ID_KEY, id);
    }

    classroomSetup.receipt = receiptFromApiPayload(data);
    classroomSetup.receipt_configured = true;
    // Le serveur répond : le repli local n'a plus lieu d'être.
    classroomSetup.receipt_local_only = false;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(classroomSetup));
    return classroomSetup.receipt;
  } catch (error) {
    console.error("Failed to load receipt config", error);
    // En cas d'erreur réseau on conserve le cache local plutôt que de faire
    // croire à une absence de configuration — ce qui inviterait à tout
    // ressaisir.
    return classroomSetup.receipt_configured ? classroomSetup.receipt : null;
  }
}

/**
 * Charge la configuration du reçu depuis le serveur.
 */
export async function loadReceiptFromApi() {
  if (pendingReceiptLoad) return pendingReceiptLoad;

  pendingReceiptLoad = performReceiptLoad().finally(() => {
    pendingReceiptLoad = null;
  });
  return pendingReceiptLoad;
}

export function parentCanAddStudent() {
  return isCollaborativeMode() && classroomSetup.allow_parent_student_creation;
}
const API_MODE = {
  COLLABORATOR: "Collaborator",
  MANUAL: "Manual",
};

/** Mode interne → valeur attendue par l'API. */
function modeToApi(mode) {
  return mode === CLASSROOM_MODE.COLLABORATIVE
    ? API_MODE.COLLABORATOR
    : API_MODE.MANUAL;
}

function modeFromApi(value) {
  if (value === API_MODE.COLLABORATOR) return CLASSROOM_MODE.COLLABORATIVE;
  if (value === API_MODE.MANUAL) return CLASSROOM_MODE.SCHOOL;
  return null;
}


async function persistMode(mode) {
  return setSfMode({ mode: modeToApi(mode) });
}

/**
 * Enregistre la configuration du module Classroom.
 */
export async function saveClassroomSetup(data) {
  if (data && data.mode) {
    await persistMode(data.mode);
  }

  Object.assign(classroomSetup, data, {
    has_configured_classroom: true,
    updated_on: new Date().toISOString(),
  });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(classroomSetup));
  return classroomSetup;
}

/**
 * Relit le mode depuis la configuration de l'établissement.
 */
export async function loadModeFromApi() {
  try {
    const config = await getSchoolConfig();
    if (!config) return classroomSetup.mode;

    const mode = modeFromApi(config.mode);
    if (!mode) return classroomSetup.mode;

    classroomSetup.mode = mode;
    // Un mode connu du serveur vaut configuration : sans cela l'écran de
    // choix se rouvrirait à chaque nouveau poste.
    classroomSetup.has_configured_classroom = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(classroomSetup));
    return mode;
  } catch (error) {
    console.error("Failed to load school config", error);
    return classroomSetup.mode;
  }
}

export async function getClassroomSetup() {
  Object.assign(classroomSetup, read());
  certificateConfigId = readCertificateId();
  receiptConfigId = readReceiptId();
  return classroomSetup;
}

export function resetClassroomSetup() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(CERTIFICATE_ID_KEY);
  localStorage.removeItem(RECEIPT_ID_KEY);
  certificateConfigId = null;
  receiptConfigId = null;
  Object.assign(classroomSetup, defaultSetup());
}
